/**
 * CreateFoodCardPage - Upload → AI Analyze → Preview → Publish
 * Complete flow for creating AI-powered FoodCards
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { 
  Camera, Upload, Sparkles, Loader, ArrowLeft, MapPin, Store, 
  Check, X, Hash, Utensils, Flame, ChefHat, Edit3, Send,
  Image as ImageIcon, RefreshCw, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hero background images
const HERO_IMAGES = [
  'https://images.unsplash.com/photo-1476224203421-9ac39bcb3327?w=1600&q=80',
  'https://images.unsplash.com/photo-1495195134817-aeb325a55b65?w=1600&q=80',
  'https://images.unsplash.com/photo-1482049016gy339-0f152934bd13?w=1600&q=80',
];

const CreateFoodCardPage = () => {
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { user } = useAuth();
  const { locationString } = useLocation();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const [heroImage] = useState(() => 'https://images.unsplash.com/photo-1476224203421-9ac39bcb3327?w=1600&q=80');

  // Prefill from recommendation/discover card navigation
  const prefill = routerLocation.state?.prefill || null;

  // Stage: 'upload' | 'analyzing' | 'preview' | 'publishing' | 'success'
  const [stage, setStage] = useState('upload');
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  
  // AI Analysis result
  const [cardData, setCardData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Form fields (editable after AI analysis) — prefilled from navigation state
  const [dishName, setDishName] = useState(prefill?.name || '');
  const [description, setDescription] = useState(prefill?.whyItFits || prefill?.description || prefill?.flavorCaption || '');
  const [cuisine, setCuisine] = useState(prefill?.cuisine || '');
  const [venueName, setVenueName] = useState(prefill?.restaurant || prefill?.venue_name || '');
  const [hashtags, setHashtags] = useState([]);
  const [vibe, setVibe] = useState(prefill?.vibe || '');
  const [caption, setCaption] = useState('');
  const [promoteThis, setPromoteThis] = useState(false);
  const [canPromote, setCanPromote] = useState(false);

  // Pre-populate image from recommendation card if available
  useEffect(() => {
    if (!prefill) return;
    const imgUrl = prefill.image || prefill.photo_url || prefill.image_url || '';
    if (imgUrl && imgUrl.startsWith('http')) {
      setImagePreview(imgUrl);
      setUploadedImageUrl(imgUrl);
    }
  }, [prefill]);

  // Check promote subscription status
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('fq_auth_token');
    fetch(`${API_URL}/api/venues/subscription/status`, {
      credentials: 'include',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCanPromote(d.can_post || false); })
      .catch(() => {});
  }, [user]);

  // Handle file selection
  const handleFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file type', description: 'Please select an image', variant: 'destructive' });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Max 10MB allowed', variant: 'destructive' });
      return;
    }

    setImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  }, [toast]);

  // Upload image to server and get URL
  const uploadImage = async () => {
    if (!imageFile) return null;

    const formData = new FormData();
    formData.append('file', imageFile);

    try {
      const res = await fetch(`${API_URL}/api/media/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      return data.url;
    } catch (e) {
      console.error('Image upload error:', e);
      return null;
    }
  };

  // Analyze with AI
  const analyzeWithAI = async () => {
    if (!imagePreview) return;

    setStage('analyzing');

    try {
      // First upload the image
      let imageUrl = uploadedImageUrl;
      if (!imageUrl && imageFile) {
        imageUrl = await uploadImage();
        setUploadedImageUrl(imageUrl);
      }

      // Convert preview to base64 (remove data:image/xxx;base64, prefix)
      const base64Data = imagePreview.split(',')[1];

      // Use Gemini Vision for enhanced analysis (allergens, health score, etc.)
      const res = await fetch(`${API_URL}/api/foodcard/analyze-gemini`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          image_base64: base64Data,
          image_url: imageUrl,
          location: locationString,
          venue_name: venueName
        })
      });

      if (!res.ok) throw new Error('Analysis failed');
      const data = await res.json();

      if (data.error && !data.dish_name) {
        throw new Error(data.error);
      }

      // Set card data
      setCardData(data);
      setDishName(data.dish_name || '');
      setDescription(data.description || '');
      setCuisine(data.cuisine || '');
      setVibe(data.vibe || '');
      setHashtags(data.hashtags || ['#FoodQuest']);
      setCaption(data.suggested_caption || '');

      setStage('preview');
    } catch (e) {
      console.error('AI analysis error:', e);
      toast({ title: 'Analysis failed', description: 'Could not identify the dish. Try a clearer photo.', variant: 'destructive' });
      setStage('upload');
    }
  };

  // Publish FoodCard
  const publishCard = async () => {
    if (!user) {
      toast({ title: 'Please sign in', description: 'You need to be signed in to create FoodCards', variant: 'destructive' });
      return;
    }

    setStage('publishing');

    try {
      const formData = new FormData();
      formData.append('dish_name', dishName);
      formData.append('description', description);
      formData.append('cuisine', cuisine);
      formData.append('venue_name', venueName);
      formData.append('location', locationString || '');
      formData.append('vibe', vibe);
      formData.append('hashtags', hashtags.join(','));
      if (promoteThis && canPromote) formData.append('is_promoted', 'true');
      
      if (uploadedImageUrl) {
        formData.append('image_url', uploadedImageUrl);
      }

      const res = await fetch(`${API_URL}/api/foodcard/create`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!res.ok) throw new Error('Failed to create');
      const data = await res.json();

      if (data.error) throw new Error(data.error);

      setStage('success');
      toast({ title: 'FoodCard Created!', description: 'Your FoodCard is now live' });

      // Redirect after delay
      setTimeout(() => navigate('/foodie'), 2000);
    } catch (e) {
      console.error('Publish error:', e);
      toast({ title: 'Failed to publish', description: e.message, variant: 'destructive' });
      setStage('preview');
    }
  };

  // Reset flow
  const resetFlow = () => {
    setStage('upload');
    setImagePreview(null);
    setImageFile(null);
    setUploadedImageUrl(null);
    setCardData(null);
    setDishName('');
    setDescription('');
    setCuisine('');
    setVenueName('');
    setHashtags([]);
    setVibe('');
    setCaption('');
    setIsEditing(false);
  };

  // Vibe options
  const vibeOptions = ['Comfort', 'Elegant', 'Casual', 'Healthy', 'Indulgent', 'Fresh', 'Rustic', 'Modern'];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header with Background Image */}
      <div className="relative" style={{ minHeight: '200px' }}>
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        />

        {/* Content */}
        <div className="relative max-w-2xl mx-auto px-4 py-6">
          {/* Semi-transparent background panel for readability */}
          <div className="bg-black/40 backdrop-blur-sm rounded-2xl p-4 w-fit">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                data-testid="create-foodcard-back"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
                  <Sparkles className="w-5 h-5 text-[#F4C430]" />
                </div>
                <div>
                  <h1 className="text-white font-bold text-xl md:text-2xl">Create FoodCard</h1>
                  <p className="text-white/80 text-sm">Snap → AI Magic → Share</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* ═══════════════════════════════════════════════════════════════════
            STAGE 1: UPLOAD
        ═══════════════════════════════════════════════════════════════════ */}
        {stage === 'upload' && (
          <div className="space-y-6">
            {/* Prefill banner */}
            {prefill && (
              <div className="bg-[#FFF5F0] border border-[#FF6B35]/20 rounded-2xl p-4 flex items-center gap-3" data-testid="prefill-banner">
                <Sparkles className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{prefill.name || prefill.caption || 'Recommended Dish'}</p>
                  <p className="text-xs text-gray-500">{imagePreview ? 'Photo loaded — review & post your FoodCard!' : 'Upload a photo to post your FoodCard'}</p>
                </div>
              </div>
            )}

            {/* Upload Area */}
            <div 
              onClick={() => fileInputRef.current?.click()}
              className={`relative rounded-3xl border-2 border-dashed transition-all cursor-pointer overflow-hidden ${
                imagePreview 
                  ? 'border-[#FF6B35] bg-[#FFF5F0]' 
                  : 'border-gray-300 hover:border-[#FF6B35] hover:bg-[#FFF5F0]/50'
              }`}
              data-testid="upload-area"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />

              {imagePreview ? (
                <div className="relative aspect-[4/3]">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <button
                    onClick={(e) => { e.stopPropagation(); resetFlow(); }}
                    className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                    <span className="text-white text-sm font-medium bg-black/30 px-3 py-1 rounded-full">
                      Tap to change photo
                    </span>
                    <span className="text-white text-xs bg-green-500/90 px-2 py-1 rounded-full flex items-center gap-1">
                      <Check className="w-3 h-3" /> Ready
                    </span>
                  </div>
                </div>
              ) : (
                <div className="py-16 px-8 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#F4C430] to-[#FF6B35] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Camera className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Snap or Upload</h3>
                  <p className="text-gray-500 mb-6">Take a photo of your food or choose from gallery</p>
                  <div className="flex items-center justify-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Camera className="w-4 h-4" />
                      <span>Camera</span>
                    </div>
                    <div className="w-px h-4 bg-gray-300" />
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Upload className="w-4 h-4" />
                      <span>Upload</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Optional Venue Input */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Store className="w-4 h-4 text-gray-400" />
                Where are you? (Optional)
              </label>
              <Input
                value={venueName}
                onChange={(e) => setVenueName(e.target.value)}
                placeholder="Restaurant or cafe name..."
                className="rounded-xl"
                data-testid="venue-input"
              />
              {locationString && (
                <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {locationString}
                </p>
              )}
            </div>

            {/* Analyze Button */}
            <Button
              onClick={analyzeWithAI}
              disabled={!imagePreview}
              className="w-full py-6 bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] hover:opacity-90 text-white font-bold text-lg rounded-2xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="analyze-button"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Let AI Identify This Dish
            </Button>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            STAGE 2: ANALYZING
        ═══════════════════════════════════════════════════════════════════ */}
        {stage === 'analyzing' && (
          <div className="text-center py-16">
            <div className="relative w-32 h-32 mx-auto mb-8">
              {imagePreview && (
                <img 
                  src={imagePreview} 
                  alt="Analyzing" 
                  className="w-full h-full object-cover rounded-2xl shadow-lg"
                />
              )}
              <div className="absolute inset-0 bg-black/40 rounded-2xl flex items-center justify-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
                  <Sparkles className="w-8 h-8 text-white animate-spin" />
                </div>
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">AI is analyzing your photo...</h3>
            <p className="text-gray-500 mb-4">Identifying dish, estimating nutrition, generating hashtags</p>
            <div className="flex items-center justify-center gap-8 text-sm text-gray-400">
              <span className="flex items-center gap-1">
                <Utensils className="w-4 h-4 animate-bounce" style={{ animationDelay: '0ms' }} />
                Dish
              </span>
              <span className="flex items-center gap-1">
                <Flame className="w-4 h-4 animate-bounce" style={{ animationDelay: '150ms' }} />
                Macros
              </span>
              <span className="flex items-center gap-1">
                <Hash className="w-4 h-4 animate-bounce" style={{ animationDelay: '300ms' }} />
                Tags
              </span>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            STAGE 3: PREVIEW & EDIT
        ═══════════════════════════════════════════════════════════════════ */}
        {stage === 'preview' && cardData && (
          <div className="space-y-6">
            {/* Card Preview */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-100">
              {/* Image */}
              <div className="relative aspect-[4/3]">
                <img 
                  src={imagePreview || cardData.image_url} 
                  alt={dishName}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                
                {/* AI Verified Badge */}
                <div className="absolute top-4 left-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
                  <Zap className="w-3 h-3" />
                  AI Validated
                </div>

                {/* Confidence */}
                {cardData.confidence && (
                  <div className="absolute top-4 right-4 bg-black/50 text-white text-xs px-3 py-1.5 rounded-full">
                    {Math.round(cardData.confidence * 100)}% match
                  </div>
                )}

                {/* Dish Name Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h2 className="text-2xl font-bold text-white mb-1">{dishName}</h2>
                  {venueName && (
                    <p className="text-white/80 text-sm flex items-center gap-1">
                      <Store className="w-3 h-3" />
                      {venueName}
                    </p>
                  )}
                </div>
              </div>

              {/* Details */}
              <div className="p-5 space-y-4">
                {/* Edit Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    {isEditing ? 'Editing details' : 'AI-generated details'}
                  </span>
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full transition-colors ${
                      isEditing 
                        ? 'bg-[#FF6B35] text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Edit3 className="w-3 h-3" />
                    {isEditing ? 'Done' : 'Edit'}
                  </button>
                </div>

                {isEditing ? (
                  /* Edit Form */
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Dish Name</label>
                      <Input
                        value={dishName}
                        onChange={(e) => setDishName(e.target.value)}
                        className="rounded-xl"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                      <Textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="rounded-xl resize-none"
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Cuisine</label>
                        <Input
                          value={cuisine}
                          onChange={(e) => setCuisine(e.target.value)}
                          className="rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Venue</label>
                        <Input
                          value={venueName}
                          onChange={(e) => setVenueName(e.target.value)}
                          className="rounded-xl"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <>
                    <p className="text-gray-600">{description}</p>
                    
                    {/* Cuisine & Vibe */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {cuisine && (
                        <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                          <Utensils className="w-3 h-3 mr-1" />
                          {cuisine}
                        </Badge>
                      )}
                      {vibe && (
                        <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                          <ChefHat className="w-3 h-3 mr-1" />
                          {vibe}
                        </Badge>
                      )}
                    </div>

                    {/* Macros */}
                    {cardData.macros && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <h4 className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                          <Flame className="w-3 h-3" />
                          Estimated Nutrition
                        </h4>
                        <div className="grid grid-cols-4 gap-3 text-center">
                          <div>
                            <p className="text-lg font-bold text-gray-900">{cardData.macros.estimated_calories}</p>
                            <p className="text-xs text-gray-500">kcal</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-blue-600">{cardData.macros.protein_g}g</p>
                            <p className="text-xs text-gray-500">Protein</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-amber-600">{cardData.macros.carbs_g}g</p>
                            <p className="text-xs text-gray-500">Carbs</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-rose-600">{cardData.macros.fat_g}g</p>
                            <p className="text-xs text-gray-500">Fat</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Enhanced Gemini Data: Health Score & Allergens */}
                    {(cardData.health_score || cardData.allergens?.length > 0) && (
                      <div className="grid grid-cols-2 gap-3">
                        {/* Health Score */}
                        {cardData.health_score && (
                          <div className="bg-emerald-50 rounded-xl p-3">
                            <h4 className="text-xs font-medium text-emerald-600 mb-1">Health Score</h4>
                            <div className="flex items-center gap-2">
                              <span className="text-2xl font-bold text-emerald-600">{cardData.health_score}</span>
                              <span className="text-sm text-emerald-500">/10</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Allergens */}
                        {cardData.allergens?.length > 0 && (
                          <div className="bg-red-50 rounded-xl p-3">
                            <h4 className="text-xs font-medium text-red-600 mb-1">Allergens</h4>
                            <div className="flex flex-wrap gap-1">
                              {cardData.allergens.map((allergen, i) => (
                                <span key={i} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                  {allergen}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Dietary Tags */}
                    {cardData.dietary_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {cardData.dietary_tags.map((tag, i) => (
                          <Badge key={i} variant="secondary" className="bg-green-100 text-green-700">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Vibe Selector */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">Vibe</label>
                  <div className="flex flex-wrap gap-2">
                    {vibeOptions.map((v) => (
                      <button
                        key={v}
                        onClick={() => setVibe(v)}
                        className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                          vibe === v 
                            ? 'bg-[#FF6B35] text-white' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Hashtags */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">Hashtags</label>
                  <div className="flex flex-wrap gap-2">
                    {hashtags.map((tag, i) => (
                      <Badge key={i} variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Suggested Caption */}
                {caption && (
                  <div className="bg-[#FFF5F0] rounded-xl p-4">
                    <h4 className="text-xs font-medium text-[#FF6B35] mb-2">Suggested Caption</h4>
                    <p className="text-gray-700 text-sm">{caption}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Promote this checkbox (subscribers only) */}
            {canPromote && (
              <label className="flex items-center gap-3 p-4 bg-gradient-to-r from-[#FFF9F0] to-[#FFF5F0] rounded-2xl border border-[#FF6B35]/15 cursor-pointer" data-testid="promote-checkbox-container">
                <input type="checkbox" checked={promoteThis} onChange={(e) => setPromoteThis(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-[#FF6B35] focus:ring-[#FF6B35]" data-testid="promote-checkbox" />
                <div>
                  <span className="text-sm font-semibold text-gray-900">Promote this FoodCard</span>
                  <p className="text-[11px] text-gray-500 mt-0.5">Boosted feed rank + wider reach + Promoted badge</p>
                </div>
              </label>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={resetFlow}
                variant="outline"
                className="flex-1 py-5 rounded-2xl"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Start Over
              </Button>
              <Button
                onClick={publishCard}
                className="flex-[2] py-5 bg-gradient-to-r from-[#F4C430] to-[#FF6B35] hover:opacity-90 text-white font-bold rounded-2xl"
                data-testid="publish-button"
              >
                <Send className="w-4 h-4 mr-2" />
                Publish FoodCard
              </Button>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            STAGE 4: PUBLISHING
        ═══════════════════════════════════════════════════════════════════ */}
        {stage === 'publishing' && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gradient-to-br from-[#F4C430] to-[#FF6B35] rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <Loader className="w-10 h-10 text-white animate-spin" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Publishing your FoodCard...</h3>
            <p className="text-gray-500">This will just take a moment</p>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            STAGE 5: SUCCESS
        ═══════════════════════════════════════════════════════════════════ */}
        {stage === 'success' && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">FoodCard Published!</h3>
            <p className="text-gray-500 mb-6">Your FoodCard is now live on your feed</p>
            <Button
              onClick={() => navigate('/foodie')}
              className="bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white rounded-2xl px-8"
            >
              View Feed
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateFoodCardPage;
